import { Button, Col, Flex, Form, Input, Row, Select, Space } from 'antd';
import { templateBtnTypes, templateHeaderOptions } from '../../constants';
import CustomModal from '../CustomModal';
import { templateLanguageOptions } from './../../constants/index';
import { createTemplate, updateTemplate } from '../../redux/actions/template';
import { useEffect, useRef, useState } from 'react';
import { extractVariables, safeJsonParse } from '../../utils';
import { ExternalLink, MessageSquare, Phone, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

const TemplateModal = ({ open, onCancel, data }) => {
    const [form] = Form.useForm();
    const headerValue = Form.useWatch('header', form);
    const [buttons, setButtons] = useState([]);
    const [loading, setLoading] = useState(false);

    const headerInputRef = useRef(null);
    const bodyInputRef = useRef(null);

    const handleVariableChange = (field, ref) => {
        const input = ref?.current?.resizableTextArea
            ? ref.current.resizableTextArea.textArea
            : ref.current?.input;

        if (!input) return;

        const cursorStart = input.selectionStart;
        const cursorEnd = input.selectionEnd;

        const currentValue = form.getFieldValue(field) || '';
        const matches = currentValue.match(/{{\d+}}/g) || [];
        const existingNumbers = matches.map((match) => parseInt(match.replace(/[{}]/g, ''), 10));
        let nextNumber = 1;
        while (existingNumbers.includes(nextNumber)) {
            nextNumber++;
        }

        const variableText = ` {{${nextNumber}}} `;
        const newValue =
            currentValue.substring(0, cursorStart) +
            variableText +
            currentValue.substring(cursorEnd);

        form.setFieldsValue({ [field]: newValue });

        setTimeout(() => {
            input.focus();
            const newCursorPos = cursorStart + variableText.length;
            input.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const handleAddButton = (sub_type) => {
        if (buttons.length >= 3) return;

        const newButton = {
            type: 'button',
            sub_type,
            index: buttons.length,
        };

        setButtons((prev) => {
            const updated = [...prev, newButton];
            return updated;
        });
    };

    const handleDeleteButton = (indexToRemove) => {
        setButtons((prevButtons) =>
            prevButtons.filter((_, idx) => idx !== indexToRemove)
        );
    };

    const handleFinish = async (values) => {
        const { name, language, header, headerText, body, footer } = values;
        setLoading(true);

        const getHeaderParams = () => {
            if (header === 'text') {
                const vars = extractVariables(headerText, 'text');
                if (vars.length > 0) {
                    return [
                        {
                            ...vars[0],
                            field: true,
                        },
                    ];
                } else {
                    return [
                        {
                            type: 'text',
                            field: false,
                        },
                    ];
                }
            }

            if (['document', 'image', 'video'].includes(header)) {
                return [{ name: '{{1}}', type: header }];
            }
            return [];
        };

        const buttonParams = buttons?.flatMap((button, index) => {
            if (button.sub_type === 'url') {
                return extractVariables(button?.url, 'text');
            }
            return [];
        });

        const headerParams = getHeaderParams();
        const bodyParams = extractVariables(body, 'text');

        const rawParams = {
            ...(headerParams.length > 0 ? { header: headerParams } : {}),
            ...(bodyParams.length > 0 ? { body: bodyParams } : {}),
            ...(buttonParams.length > 0 ? { button: buttonParams } : {}),
        };

        const hasParams = Object.keys(rawParams).length > 0;

        const payload = {
            name,
            language,
            content: body,
            ...(header === 'text' && headerText ? { header: headerText } : {}),
            ...(footer ? { footer } : {}),
            ...(hasParams ? { params: JSON.stringify(rawParams) } : {}),
            ...(data ? { id: data.id } : {}),
            ...(buttons.length > 0 ? { buttons: JSON.stringify(buttons) } : {}),
        };

        const res = data ? await updateTemplate(payload) : await createTemplate(payload);
        setLoading(false);

        if (res?.ack) onCancel();
    };

    useEffect(() => {
        if (data) {
            const headerType = safeJsonParse(data.params)?.header?.[0]?.type ?? 'none';
            form.setFieldsValue({
                name: data.name,
                language: data.language_code,
                header: headerType,
                headerText: headerType === 'text' ? data.header : undefined,
                body: data.content,
                footer: data.footer,
            });
            if (data.buttons) {
                const parsedButtons = safeJsonParse(data.buttons) || [];
                setButtons(parsedButtons);
            }
        }
    }, [data]);

    return (
        <CustomModal
            title={data ? 'Update Template' : 'Create Template'}
            open={open}
            onCancel={onCancel}
            okText={data ? 'Update' : 'Create'}
            onOk={() => form.submit()}
            className="template-modal"
            okButtonProps={{ loading }}
            cancelButtonProps={{ disabled: loading }}
            width={800}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish} size="large">
                <Form.Item
                    name="name"
                    label="Template Name"
                    rules={[{ required: true, message: 'Please input the template name!' }]}
                >
                    <Input placeholder="Enter template name" />
                </Form.Item>
                <Form.Item
                    name="language"
                    label="Language"
                    rules={[{ required: true, message: 'Please select a language' }]}
                >
                    <Select options={templateLanguageOptions} placeholder="Select language" />
                </Form.Item>
                <Row gutter={16}>
                    <Col span={headerValue === 'text' ? '4' : '24'}>
                        <Form.Item name="header" label="Header">
                            <Select options={templateHeaderOptions} defaultValue="none" />
                        </Form.Item>
                    </Col>

                    {headerValue === 'text' && (
                        <Col span={20}>
                            <Form.Item
                                name="headerText"
                                label="Text"
                                rules={[{ required: true, message: 'Please input the header text!' }]}
                            >
                                <Input
                                    ref={headerInputRef}
                                    placeholder="Enter text"
                                    suffix={
                                        <Button
                                            variant="filled"
                                            color="primary"
                                            size="small"
                                            onClick={() => handleVariableChange('headerText', headerInputRef)}
                                        >
                                            Add Variable
                                        </Button>
                                    }
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>

                <Form.Item
                    name="body"
                    label="Message"
                    rules={[{ required: true, message: 'Please input the template content!' }]}
                >
                    <div className="text-area-container">
                        <Flex vertical gap={4} align="end">
                            <Input.TextArea
                                ref={bodyInputRef}
                                placeholder="Enter text"
                                value={Form.useWatch('body', form)}
                                style={{ resize: 'none' }}
                                rows={4}
                                maxLength={1024}
                                showCount
                                count
                            />
                            <Button
                                variant="filled"
                                color="primary"
                                size="small"
                                onClick={() => handleVariableChange('body', bodyInputRef)}
                                className="add-variable-btn"
                            >
                                Add Variable
                            </Button>
                        </Flex>
                    </div>
                </Form.Item>
                <Form.Item name="footer" label="Footer">
                    <Input placeholder="Enter text" />
                </Form.Item>

                <Form.Item label="Button">
                    <Flex gap="small" wrap>
                        <Button
                            disabled={buttons.length >= 3}
                            color="green"
                            variant="dashed"
                            icon={<MessageSquare size={16} />}
                            onClick={() => handleAddButton('quick_reply')}
                        >
                            Quick Reply
                        </Button>
                        <Button
                            disabled={buttons.length >= 3}
                            color="blue"
                            variant="dashed"
                            icon={<ExternalLink size={16} />}
                            onClick={() => handleAddButton('url')}
                        >
                            Visit Website
                        </Button>
                        <Button
                            disabled={buttons.length >= 3}
                            color="cyan"
                            variant="dashed"
                            icon={<Phone size={16} />}
                            onClick={() => handleAddButton('phone_number')}
                        >
                            Phone Number
                        </Button>
                    </Flex>
                </Form.Item>


                <Flex vertical gap="small" className="button-container mt-4">
                    <AnimatePresence>
                        {buttons?.map((button, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, height: 0, y: -10 }}
                                animate={{ opacity: 1, height: 'auto', y: 0 }}
                                exit={{ opacity: 0, height: 0, y: -10 }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                            >
                                <Flex gap="small" align="center" className="button-item px-3 py-2">
                                    <Form.Item label="Button Text">
                                        <Input
                                            placeholder="Button text"
                                            value={button.text || ''}
                                            onChange={(e) => {
                                                const newButtons = [...buttons];
                                                newButtons[index].text = e.target.value;
                                                setButtons(newButtons);
                                            }}
                                        />
                                    </Form.Item>

                                    {button.sub_type !== 'quick_reply' && (
                                        <Form.Item
                                            label={templateBtnTypes[button.sub_type]}
                                            rules={[
                                                { required: true },
                                                button.sub_type === 'url' && { type: 'url', warningOnly: true },
                                            ]}
                                            onChange={(e) => {
                                                const newButtons = [...buttons];
                                                newButtons[index][button.sub_type] = e.target.value;
                                                setButtons(newButtons);
                                            }}
                                        >
                                            <Input
                                                placeholder={
                                                    button.sub_type === 'url'
                                                        ? 'https://www.example.com/{{1}}'
                                                        : '+1234567890'
                                                }
                                                value={button[button.sub_type] || ''}
                                            />
                                        </Form.Item>
                                    )}
                                    <Space>
                                        <Button
                                            danger
                                            type="link"
                                            icon={<X size={22} />}
                                            onClick={() => handleDeleteButton(index)}
                                        />
                                    </Space>
                                </Flex>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </Flex>
            </Form>
        </CustomModal>
    );
};

export default TemplateModal;
